import React, { useState, useCallback } from 'react'
import { Box, VStack, useDisclosure } from '@chakra-ui/react'
import { FiPlus } from 'react-icons/fi'
import { useAdminGuard } from '@/hooks/useAdminGuard'
import PageHeader from '@/components/Common/PageHeader'
import PlansStats from '@/components/Admin/PlansStats'
import PlansGrid from '@/components/Admin/PlansGrid'
import usePlans, { Plan } from '@/hooks/usePlans'
import CreatePlanModal from '@/components/Admin/CreatePlanModal'
import EditPlanModal from '@/components/Admin/EditPlanModal'

export default function AdminPlansPage() {
  const { isAuthorized, ForbiddenView } = useAdminGuard()
  const { plans, loading, stats, deletePlan, refreshPlans } = usePlans()
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)

  // Modal states
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure()

  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()

  // Event handlers
  const handleEdit = useCallback(
    (plan: Plan) => {
      setSelectedPlan(plan)
      onEditOpen()
    },
    [onEditOpen]
  )

  const handleDelete = useCallback(
    async (plan: Plan) => {
      await deletePlan(plan)
    },
    [deletePlan]
  )

  const handlePlanSuccess = useCallback(() => {
    refreshPlans()
    onCreateClose()
    onEditClose()
    setSelectedPlan(null)
  }, [refreshPlans, onCreateClose, onEditClose])

  if (!isAuthorized) return ForbiddenView

  return (
    <Box p={{ base: 4, md: 6 }}>
      <VStack spacing={8} align="stretch">
        {/* Page Header */}
        <PageHeader
          title="Plan Management"
          description="Manage subscription plans and organization limits"
          actions={[
            {
              label: 'Create Plan',
              icon: <FiPlus />,
              onClick: onCreateOpen,
            },
          ]}
        />

        {/* Stats Overview */}
        <PlansStats stats={stats} />

        {/* Plans Grid */}
        <PlansGrid plans={plans} loading={loading} onEdit={handleEdit} onDelete={handleDelete} />

        {/* Modals */}
        <CreatePlanModal
          isOpen={isCreateOpen}
          onClose={onCreateClose}
          onSuccess={handlePlanSuccess}
        />

        {selectedPlan && (
          <EditPlanModal
            isOpen={isEditOpen}
            onClose={onEditClose}
            plan={selectedPlan}
            onSuccess={handlePlanSuccess}
          />
        )}
      </VStack>
    </Box>
  )
}
